import React, { useEffect, useState } from "react";
import { useRefreshRead } from "@/hooks/useRefreshRead";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatINRFull } from "@/lib/format";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { LoadingState, EmptyState } from "@/components/v3/States";

const inputCls = "w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm";

export default function Recon() {
  const [csv, setCsv] = useState("date,amount,utr,narration\n");
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const reload = () => { setLoadError(false); return api.get("/v3/recon/summary").then((r) => setData(r.data)).catch(() => setLoadError(true)); };
  useEffect(() => { reload(); }, []);
  useRefreshRead(reload);
  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  const doImport = () => {
    const lines = csv.trim().split("\n");
    const head = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const rows = lines.slice(1).filter(Boolean).map((l) => {
      const v = l.split(",");
      return Object.fromEntries(head.map((h, i) => [h, (v[i] || "").trim()]));
    });
    if (!rows.length) { toast.error("Add at least one data row"); return; }
    api.post("/v3/recon/import", { rows })
      .then((r) => { toast.success(`Imported ${r.data.results.length} — ${r.data.results.filter((x) => x.match_type !== "unmatched").length} matched`); reload(); })
      .catch(err);
  };
  const resolve = (id) => api.post(`/v3/exceptions/${id}/resolve`).then(() => { toast.success("Resolved"); reload(); }).catch(err);
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1300px]" data-testid="recon-page">
      {loadError && <div className="report-warning" data-testid="recon-error" role="alert">Reconciliation unavailable; do not infer zero exceptions. <button data-testid="recon-retry" className="rf-link" onClick={reload}>Retry</button></div>}
      <header className="mb-6">
        <div className="ceo-label !text-[#b3122a]">Operate · Build 8</div>
        <h1 className="ceo-fig text-3xl text-[#111111] mt-1">Bank Reconciliation</h1>
        <p className="text-[13px] text-slate-500 mt-1.5">Mock bank-statement import (CSV) matched against V3 test receipts by UTR then amount. No live bank API. <SourceBadge source="MOCK" /></p>
      </header>
      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="recon-import-card">
          <div className="font-semibold text-slate-900 text-sm mb-2">Import bank CSV (date,amount,utr,narration)</div>
          <textarea data-testid="recon-csv-input" rows={6} className={`${inputCls} font-mono text-[12px]`} value={csv} onChange={(e) => setCsv(e.target.value)} />
          <button data-testid="recon-import-btn" onClick={doImport} className="mt-2 bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2">Import & match</button>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="recon-exceptions-card">
          <div className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900 text-sm">Exception queue ({data ? data.open_exceptions : "…"} open)</div>
          {!data ? <LoadingState /> : data.exceptions.length === 0 ? <EmptyState title="No exceptions" /> : (
            <div className="max-h-64 overflow-auto">
              {data.exceptions.map((e) => (
                <div key={e.v3_id} className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-2 min-w-0" data-testid={`recon-exception-${e.v3_id}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${e.status === "open" ? "bg-[#b3122a]" : "bg-emerald-500"}`} />
                  <span className="text-[12px] text-slate-700 flex-1 min-w-0 break-words">{e.details}</span>
                  {e.status === "open" ? (
                    <button data-testid={`exception-resolve-${e.v3_id}`} onClick={() => resolve(e.v3_id)} className="text-[11px] font-bold text-emerald-700 border border-emerald-300 rounded px-2 py-1">Resolve</button>
                  ) : <span className="text-[10px] font-bold uppercase text-emerald-600">resolved</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="recon-matches-table">
        <div className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900 text-sm">Match results</div>
        {!data ? <LoadingState /> : data.matches.length === 0 ? <EmptyState title="No imports yet" /> : (
          <table className="report-table recon-responsive-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
              <th className="px-4 py-2.5">UTR</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5">Invoice</th><th className="px-4 py-2.5">Match</th></tr></thead>
            <tbody>
              {data.matches.map((m) => (
                <tr key={m.v3_id} className="border-b border-slate-100" data-testid={`recon-match-${m.v3_id}`}>
                  <td data-label="Reference" className="font-mono text-slate-500">{m.utr || "—"}</td>
                  <td data-label="Amount" className="tabular-nums" data-testid={`recon-amount-${m.v3_id}`}>{formatINRFull(m.amount)}</td>
                  <td data-label="Invoice">{m.invoice_no || "—"}</td>
                  <td data-label="Match" className={`font-bold uppercase ${m.match_type === "unmatched" ? "text-[#b3122a]" : "text-emerald-700"}`}>{m.match_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
